import cn from 'classnames';
import { useState, useRef } from 'react';
import { Link, useLocation } from "react-router-dom";

/* i18n */
import { useIntl } from 'react-intl';

/* Styles */
import styles from 'styles/recommendations-header.module.scss';
import layoutStyles from 'styles/layout.module.scss';

/* Assets */
import thumbsUp from 'assets/images/ThumbsUp_Small_Outline.svg';
import thumbsDown from 'assets/images/ThumbsDown_Small_Outline.svg';
import pencil from 'assets/images/Pencil.svg';
import chevronDown from 'assets/images/Chevron_Down.svg';
import chevronUp from 'assets/images/Chevron_Up.svg';
import chevronRight from 'assets/images/Chevron_Right.svg';
import chevronLeft from 'assets/images/Chevron_Left.svg';

/* Analytics */
import { trackEvent } from 'lib/analytics';

/* Material UI and other UI Dependencies */
import { Box, withWidth, Typography, Container } from '@material-ui/core';

/* Redux */
import { useSelector, useDispatch } from 'react-redux';
import { setActiveSoc } from 'app/slices/jobsSlice';

const RecommendationsHeader =({locale, width}) => {

  const dispatch = useDispatch();
  const location = useLocation();
  const activePath = location.pathname.split('/').reverse()[0];

  const [ showContent, setShowContent ] = useState(false);
  const [ showRightArrow, setShowRightArrow ] = useState(true);
  const [ showLeftArrow, setShowLeftArrow ] = useState(false);

  /* Refs to get menu's scroll position */
  const itemsContainer = useRef(null);  
  
  const intl = useIntl();
  const urlPrefix = intl.locale === 'en' ? '' : `/${intl.locale}`;

  const preferredName = useSelector((state) => state.app.preferredName);
  const experience = useSelector((state) => state.experience.form);
  const educationDescriptions = {
    "11": intl.formatMessage({id:'forms.onboarding.education.noDiploma'}),
    "12": intl.formatMessage({id:'forms.onboarding.education.diploma'}),
    "13": intl.formatMessage({id:'forms.onboarding.education.someCollege'}),
    "14": intl.formatMessage({id:'forms.onboarding.education.assocDegree'}),
    "16": intl.formatMessage({id:'forms.onboarding.education.bachDegree'}),
    "17": intl.formatMessage({id:'forms.onboarding.education.mastersDegree'}),
    "20": intl.formatMessage({id:'forms.onboarding.education.advancedDegree'}),
  };

  const smallScreen = ['xs'].indexOf(width) > -1;

  const navScrollHandler = (e) => {
    const endOfScroll = e.target.scrollWidth - e.target.clientWidth;
    const currentPosition = e.target.scrollLeft;
    if (currentPosition === endOfScroll) {
      setShowRightArrow(false);
    } else {
      setShowRightArrow(true);
    }

    if (currentPosition === 0) {
      setShowLeftArrow(false);
    } else {
      setShowLeftArrow(true);
    }
  }

  const arrowRightClickHandler = (e) => {
    const endOfScroll = itemsContainer.current.scrollWidth - itemsContainer.current.clientWidth;
    const currentPosition = itemsContainer.current.scrollLeft;
    const hop = 90;
    if (currentPosition + hop <= endOfScroll) {
      itemsContainer.current.scrollLeft = currentPosition + hop;
    } else {
      itemsContainer.current.scrollLeft = endOfScroll;
    }
  }

  const arrowLeftClickHandler = (e) => {
    const currentPosition = itemsContainer.current.scrollLeft;
    const hop = 90;
    if (currentPosition - hop >= 0) {
      itemsContainer.current.scrollLeft = currentPosition - hop;
    } else {
      itemsContainer.current.scrollLeft = 0;
    }
  }
  const handleMenuItemClick = (page) => () => {
    const eventName = `HORIZONTAL_MENU_${page}`;
    trackEvent(eventName);
    if (page === 'JOBS') {
      dispatch(setActiveSoc(''));
    }
  }

  const handleShowExperience = () => {
    setShowContent(!showContent);
    const eventName = !showContent ? 'MOBILE_SHOWEXPERIENCE' : 'MOBILE_HIDEEXPERIENCE';
    trackEvent(eventName);
  }

  return(
    <>
      <div className={styles.header}>
        <Container maxWidth="lg">
          <div className={styles.header__content}>
            <Typography align="center" variant="h1" className={styles.headerTitle} gutterBottom>
              { preferredName ?
                  intl.formatMessage(
                    {id: 'recommendations.titlePersonalized', description:'string'},
                    {name: preferredName}
                  ) :
                  intl.formatMessage(
                    {id: 'recommendations.title', description:'string'}
                  )
              }
            </Typography>
            <div className={cn({
              [styles.accordionContent]: true,
              [styles.accordionContent__visible]: showContent && smallScreen,
              [styles.accordionContent__hidden]: !showContent && smallScreen,
            })}>
              <Typography align="center">
                {intl.formatMessage(
                  {id:'recommendations.subtitleLine1', description:'string'}
                )}
              </Typography>
              <Typography align="center" gutterBottom>
                <span>{intl.formatMessage(
                  {id:'recommendations.subtitleLine2', description:'string'}
                )}</span>
                <Box component="div" display="inline-block">
                  [<img alt="Thumbs Up" className={styles.thumbs} src={thumbsUp} />
                  <img alt="Thumbs Down" className={styles.thumbs} src={thumbsDown} />]
                </Box>                
                <span>{intl.formatMessage(
                  {id:'recommendations.subtitleLine3', description:'string'}
                )}</span>
              </Typography>
              <Typography align="center" className={styles.experienceTag__container} gutterBottom>
                { experience.soc_title &&
                  <span className={styles.experienceTag}>
                    {experience.soc_title}
                  </span>
                }
                { experience.years_experience &&
                  <span className={styles.experienceTag}>
                    {experience.years_experience} {intl.formatMessage({id:'recommendations.yearsExperience',description:'string'})}
                  </span>
                }
                { typeof(educationDescriptions[experience.education_level]) !== 'undefined' &&
                  <span className={styles.experienceTag}>
                    {educationDescriptions[experience.education_level]}
                  </span>
                }
                <Link to={`${urlPrefix}/experience`} className={cn(layoutStyles.link, styles.editLink)}>{intl.formatMessage({id: 'layout.header.edit'})}</Link>
                <span> <img alt="Pencil" className={styles.pencil} src={pencil} /></span>
              </Typography>
            </div>
          </div>
          <div className={styles.showButton} onClick={handleShowExperience}>
            {intl.formatMessage(
              {id: (
                showContent ? 'recommendations.hideExperience' : 'recommendations.showExperience'
                ), description:'string'}
            )}
            <span> {
              showContent ?
                <img alt="Hide content" className={styles.chevron} src={chevronUp} />
                : <img alt="Show content" className={styles.chevron} src={chevronDown} />
              }</span>
          </div>
        </Container>
      </div>
      <nav className={styles.mainNav}>
          <span
            onClick={arrowLeftClickHandler}
            className={cn({
              [styles.chevronLink]: true,
              [styles.chevronLinkLeft]: true,
              [styles.hidden]: !showLeftArrow
            })}
          >
            <img alt="Arrow left" src={chevronLeft} />
          </span>
          <span
            onClick={arrowRightClickHandler}
            className={cn({
              [styles.chevronLink]: true,
              [styles.chevronLinkRight]: true,
              [styles.hidden]: !showRightArrow
            })}
          >
            <img alt="Arrow right" src={chevronRight} />
          </span>
        <Container maxWidth="lg">
          <div ref={itemsContainer} className={styles.mainNav__itemsContainer} onScroll={navScrollHandler}>
            <Link to={`${urlPrefix}/careers`} onClick={handleMenuItemClick('CAREERPATHS')} className={cn({
              [styles.mainNav__item]: true,
              [styles.mainNav__item__active]: activePath === 'careers'
            })}>
              <span
                className={cn({
                  [styles.breakSpace]: (
                    intl.formatMessage({id: 'layout.menu.careers', description:'string'}).length > 12
                  )
                })}
              >
                {intl.formatMessage({id: 'layout.menu.careers', description:'string'})}
              </span>
            </Link>
            <Link to={`${urlPrefix}/jobs`} onClick={handleMenuItemClick('JOBS')} className={cn({
              [styles.mainNav__item]: true,
              [styles.mainNav__item__active]: activePath === 'jobs'
            })}>
              <span
                className={cn({
                  [styles.breakSpace]: (
                    intl.formatMessage({id: 'layout.menu.jobs', description:'string'}).length > 12
                  )
                })}
              >
                {intl.formatMessage({id: 'layout.menu.jobs', description:'string'})}
              </span>
            </Link>
            <Link to={`${urlPrefix}/training`} onClick={handleMenuItemClick('TRAINING')} className={cn({
              [styles.mainNav__item]: true,
              [styles.mainNav__item__active]: activePath === 'training'
            })}>
              <span
                className={cn({
                  [styles.breakSpace]: (
                    intl.formatMessage({id: 'layout.menu.training', description:'string'}).length > 12
                  )
                })}
              >
                {intl.formatMessage({id: 'layout.menu.training', description:'string'})}
              </span>
            </Link>
            <Link to={`${urlPrefix}/experience`} onClick={handleMenuItemClick('SKILLSEXPERIENCE')} className={cn({
              [styles.mainNav__item]: true,
              [styles.mainNav__item__active]: activePath === 'experience'
            })}>
              <span
                className={cn({
                  [styles.breakSpace]: (
                    intl.formatMessage({id: 'layout.menu.experience', description:'string'}).length > 12
                  )
                })}
                dangerouslySetInnerHTML={{ __html: intl.formatMessage({id: 'layout.menu.experience', description:'string'}) }}
              />
            </Link>
            <Link to={`${urlPrefix}/ratings`} onClick={handleMenuItemClick('RATINGS')} className={cn({
              [styles.mainNav__item]: true,
              [styles.mainNav__item__active]: activePath === 'ratings'
            })}>
              <span
                className={cn({
                  [styles.breakSpace]: (
                    intl.formatMessage({id: 'layout.menu.ratings', description:'string'}).length > 12
                  )
                })}
              >
                {intl.formatMessage({id: 'layout.menu.ratings', description:'string'})}
              </span>
            </Link>
            <a
              href="https://www.backtoworkri.com/"
              onClick={handleMenuItemClick('JOBCOACH')}
              className={cn({
                [styles.mainNav__item]: true
              })}
              target="_blank"
              rel="noreferrer"
            >
              <span
                className={cn({
                  [styles.breakSpace]: (
                    intl.formatMessage({id: 'layout.menu.coach', description:'string'}).length > 12
                  )
                })}
              >
                {intl.formatMessage({id: 'layout.menu.coach', description:'string'})}
                &nbsp;<img src="/images/external-link-alt-solid.svg" width="14" height="13" />
              </span>
            </a>
          </div>
        </Container>
      </nav>
    </>
  )
}

export default withWidth()(RecommendationsHeader);